/**
 * Fetches weekly working hours for the selected interviewers and
 * returns the common availability windows (union for 1:1, intersection
 * for panel). Stays empty for self-schedule modes.
 */
import { useMemo } from 'react';
import { useInterviewerScheduleQuery } from '@/api/interviewerSchedule';
import type { InterviewerScheduleByUserId } from '@/api/interviewerSchedule/types';
import {
  computeCommonAvailability,
  type InterviewKind,
  type Interval,
} from '../../availability';

export interface UseCommonAvailabilityArgs {
  weekStartIso: string;
  timezone: string;
  interviewerIds: number[];
  interviewKind: InterviewKind;
  enabled?: boolean;
}

export interface UseCommonAvailabilityResult {
  availability: Interval[];
  isLoading: boolean;
  /**
   * Raw per-interviewer working-hours map from the API. Surfaced
   * alongside the post-intersection `availability` so callers that
   * need union/intersection-aware classification — e.g. payload-time
   * `is_custom` / `override_type` computation — can apply the kind's
   * aggregation rule themselves. Empty `{}` when the query is disabled
   * or hasn't resolved.
   */
  scheduleByUserId: InterviewerScheduleByUserId;
}

const EMPTY_SCHEDULE: InterviewerScheduleByUserId = {};

export function useCommonAvailability({
  weekStartIso,
  timezone,
  interviewerIds,
  interviewKind,
  enabled = true,
}: UseCommonAvailabilityArgs): UseCommonAvailabilityResult {
  const { data: scheduleByUserId = EMPTY_SCHEDULE, isLoading } =
    useInterviewerScheduleQuery({
      weekStartIso,
      timezone,
      interviewerIds,
      enabled,
    });

  const availability = useMemo(
    () =>
      computeCommonAvailability({
        scheduleByUserId,
        timezone,
        interviewKind,
      }),
    [scheduleByUserId, timezone, interviewKind]
  );

  return { availability, isLoading, scheduleByUserId };
}
